import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { HCVProgram, RentersInsuranceRecord } from '@repo/shared-types';
import { ComplianceDocument, ComplianceDocumentUpload } from '@repo/shared-types/compliance';
import type { RentersInsuranceFilters } from '../../services/api/compliance';
import { useToast } from '../../components/common/ToastContext';
import { hcvProgramSchema, complianceDocumentSchema } from '@repo/shared-types/compliance/schemas';
import { validateEntity, validateEntityArray } from '@repo/shared-utils';
import { logger } from '@repo/shared-utils';

export const useHCVPrograms = () => {
  return useQuery({
    queryKey: ['compliance', 'hcv'],
    queryFn: async () => {
      const data = await api.compliance.hcv.list();
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(hcvProgramSchema, data);
      if (!validation.valid) {
        logger.error('Invalid HCV programs query response:', validation.errors);
        throw new Error('Invalid HCV programs data received from API');
      }
      return validation.data! as HCVProgram[];
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useHCVProgram = (id: string | undefined) => {
  return useQuery({
    queryKey: ['compliance', 'hcv', id],
    queryFn: async () => {
      const data = await api.compliance.hcv.get(id!);
      // ✅ Validate query response with Zod schema
      if (data) {
        const validation = validateEntity(hcvProgramSchema, data);
        if (!validation.valid) {
          logger.error('Invalid HCV program query response:', validation.errors);
          throw new Error('Invalid HCV program data received from API');
        }
        return validation.data! as HCVProgram;
      }
      return null;
    },
    enabled: !!id,
  });
};

export const useCreateHCVProgram = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (program: HCVProgram) => api.compliance.hcv.create(program),
    onSuccess: (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(hcvProgramSchema, data);
      if (!validation.valid) {
        logger.error('Invalid HCV program response from API:', validation.errors);
        showToast('HCV Program created but received invalid data from server', 'error');
        queryClient.invalidateQueries({ queryKey: ['compliance', 'hcv'] });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['compliance', 'hcv'] });
      showToast('HCV Program created successfully', 'success');
    },
    onError: () => {
      showToast('Failed to create HCV Program', 'error');
    }
  });
};

export const useUpdateHCVProgram = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<HCVProgram> }) =>
      api.compliance.hcv.update(id, updates),
    onSuccess: (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(hcvProgramSchema, data);
      if (!validation.valid) {
        logger.error('Invalid HCV program response from API:', validation.errors);
        showToast('HCV Program updated but received invalid data from server', 'error');
        queryClient.invalidateQueries({ queryKey: ['compliance', 'hcv'] });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['compliance', 'hcv'] });
      showToast('HCV Program updated successfully', 'success');
    },
    onError: () => {
      showToast('Failed to update HCV Program', 'error');
    }
  });
};

export const useDeleteHCVProgram = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.compliance.hcv.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance', 'hcv'] });
      showToast('HCV Program deleted successfully', 'success');
    },
    onError: () => {
      showToast('Failed to delete HCV Program', 'error');
    }
  });
};

export const useResolveHcvCompliance = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (body: {
      programId: string;
      resolutionReason: string;
      nextActionDate?: string;
      impactSummary?: string;
    }) => api.compliance.hcv.resolveCompliance(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance', 'hcv'] });
      queryClient.invalidateQueries({ queryKey: ['governance', 'logs'] });
      showToast('Compliance resolved and recorded in Governance.', 'success');
    },
    onError: () => {
      showToast('Could not resolve compliance.', 'error');
    },
  });
};

export const useReopenHcvCompliance = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (body: { programId: string; reason: string }) => api.compliance.hcv.reopenCompliance(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance', 'hcv'] });
      queryClient.invalidateQueries({ queryKey: ['governance', 'logs'] });
      showToast('Compliance reopened.', 'success');
    },
    onError: () => {
      showToast('Could not reopen compliance.', 'error');
    },
  });
};

// Compliance Documents Hooks
export const useComplianceDocuments = () => {
  return useQuery({
    queryKey: ['compliance', 'documents'],
    queryFn: async () => {
      const data = await api.compliance.documents.list();
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(complianceDocumentSchema, data);
      if (!validation.valid) {
        const errorCount = validation.errors?.length || 0;
        const errorDetails = validation.errors?.slice(0, 3).map(({ index, error }) => {
          const doc = data[index];
          const fieldErrors = error.errors.map(e => `${e.path.join('.')}: ${e.message}`).join('; ');
          return `Record ${index} (ID: ${doc?.id || 'unknown'}): ${fieldErrors}`;
        }).join(' | ') || 'Unknown validation errors';

        logger.warn('Compliance documents query response did not match schema; returning empty list', {
          queryKey: 'compliance.documents',
          totalRecords: Array.isArray(data) ? data.length : 0,
          invalidRecords: errorCount,
          errorDetails: errorCount > 3 ? `${errorDetails}... (${errorCount - 3} more)` : errorDetails,
        });
        return [] as ComplianceDocument[];
      }
      return validation.data! as ComplianceDocument[];
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useComplianceDocument = (id: string | undefined) => {
  return useQuery({
    queryKey: ['compliance', 'documents', id],
    queryFn: async () => {
      const data = await api.compliance.documents.get(id!);
      // ✅ Validate query response with Zod schema
      if (data) {
        const validation = validateEntity(complianceDocumentSchema, data);
        if (!validation.valid) {
          const fieldErrors = validation.errors?.errors.map(e => `${e.path.join('.')}: ${e.message}`).join('; ') || 'Unknown validation errors';
          logger.warn('Compliance document query response did not match schema; returning null', {
            queryKey: `compliance.documents.${id}`,
            documentId: id,
            fieldErrors,
          });
          return null;
        }
        return validation.data! as ComplianceDocument;
      }
      return null;
    },
    enabled: !!id,
  });
};

export const useCreateComplianceDocument = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (document: ComplianceDocumentUpload) => api.compliance.documents.create(document),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance', 'documents'] });
      showToast('Compliance document created successfully', 'success');
    },
    onError: () => {
      showToast('Failed to create compliance document', 'error');
    }
  });
};

export const useUpdateComplianceDocument = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<ComplianceDocument> }) =>
      api.compliance.documents.update(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance', 'documents'] });
      showToast('Compliance document updated successfully', 'success');
    },
    onError: () => {
      showToast('Failed to update compliance document', 'error');
    }
  });
};

export const useDeleteComplianceDocument = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.compliance.documents.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance', 'documents'] });
      showToast('Compliance document deleted successfully', 'success');
    },
    onError: () => {
      showToast('Failed to delete compliance document', 'error');
    }
  });
};

// Renters insurance hooks
export const useRentersInsuranceRecords = (filters?: RentersInsuranceFilters) => {
  return useQuery({
    queryKey: ['compliance', 'renters-insurance', filters ?? {}],
    queryFn: () => api.compliance.rentersInsurance.list(filters),
    staleTime: 1000 * 60 * 2,
  });
};

export const useRentersInsuranceByTenant = (tenantId: string | null) => {
  return useQuery({
    queryKey: ['compliance', 'renters-insurance', 'tenant', tenantId],
    queryFn: () => api.compliance.rentersInsurance.getByTenant(tenantId!),
    enabled: !!tenantId,
  });
};

export const useCreateRentersInsuranceRecord = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (body: { tenantId: string; leaseId?: string; expirationDate: string; documentUrl?: string }) =>
      api.compliance.rentersInsurance.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance', 'renters-insurance'] });
      showToast('Renters insurance record added', 'success');
    },
    onError: (e: Error) => {
      showToast(e.message || 'Failed to add record', 'error');
    },
  });
};

export const useUpdateRentersInsuranceRecord = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: { expirationDate?: string; documentUrl?: string } }) =>
      api.compliance.rentersInsurance.update(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance', 'renters-insurance'] });
      showToast('Renters insurance updated', 'success');
    },
    onError: (e: Error) => {
      showToast(e.message || 'Failed to update', 'error');
    },
  });
};


